import { useCategories, getCategoryBarColor } from "../../../../shared/stores/category-store";

interface CategoryStatsProps {
  readonly categoryCounts: Record<string, number>;
  readonly maxCategoryCount: number;
}

export function CategoryStats({
  categoryCounts,
  maxCategoryCount,
}: CategoryStatsProps) {
  const { categories, labels } = useCategories();

  // Calculer le total pour les pourcentages
  const totalCount = categories.reduce((sum, cat) => sum + (categoryCounts[cat] ?? 0), 0);

  // Trier par valeur décroissante
  const sortedCategories = [...categories].sort((a, b) => (categoryCounts[b] ?? 0) - (categoryCounts[a] ?? 0));

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">Tâches par catégorie</h3>
        <span className="text-[10px] text-ink-faint uppercase tracking-wide">
          {totalCount} totale{totalCount !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Liste des catégories */}
      <div className="p-5 space-y-4">
        {sortedCategories.map((cat, index) => {
          const count = categoryCounts[cat] ?? 0;
          const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
          const barColor = getCategoryBarColor(cat);
          const barPct = maxCategoryCount > 0 ? (count / maxCategoryCount) * 100 : 0;

          if (count === 0) return null;

          return (
            <div key={cat} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Indice du rang */}
                  <span className={`text-[10px] font-bold ${
                    index === 0 ? "text-accent" : "text-ink-faint"
                  }`}>
                    #{index + 1}
                  </span>
                  <span className="text-sm font-medium text-ink">{labels[cat]}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{count}</span>
                  <span className="text-xs text-ink-faint">({percentage}%)</span>
                </div>
              </div>
              {/* Barre de progression */}
              <div className="w-full h-2.5 bg-surface-alt rounded-full overflow-hidden shadow-inner">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Légende des couleurs si vide */}
      {totalCount === 0 && (
        <div className="p-8 text-center">
          <p className="text-xs text-ink-faint">Aucune tâche pour le moment</p>
        </div>
      )}
    </div>
  );
}
